import type {
    PublicKeyCredentialCreationOptionsJSON,
    PublicKeyCredentialRequestOptionsJSON
} from '@simplewebauthn/browser';
import {startAuthentication, startRegistration} from '@simplewebauthn/browser';
import {BooksitoutServer} from '../config/BooksitoutServer';
import LoginSuccessResponse from "../routes/login/LoginSuccessResponse";

export interface PasskeyAuthResult {
    success: boolean;
    loginData?: PasskeyVerificationResult;
    error?: string;
}

export interface PasskeyLoginResult {
    success: boolean;
    loginData?: LoginSuccessResponse
    error?: string;
}

export interface PasskeyRegistrationOptions extends PublicKeyCredentialCreationOptionsJSON {
}

export interface PasskeyAuthenticationOptions extends PublicKeyCredentialRequestOptionsJSON {
}

export interface PasskeyVerificationResult {
    verified: boolean;
    accessToken?: string;
    refreshToken?: string;
    name?: string;
    profileImage?: string;
    ttl?: Date;
    isInternal?: boolean;
    error?: string;
}

export const isPasskeySupported = (): boolean => {
    return window.PublicKeyCredential !== undefined &&
        typeof window.PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable === 'function';
};

export const registerPasskey = async (): Promise<PasskeyAuthResult> => {
    try {
        if (!isPasskeySupported()) {
            return {success: false, error: 'Passkey not supported on this device'};
        }

        const response = await BooksitoutServer.post<PasskeyRegistrationOptions>('/v1/login/passkey/register/begin');
        const options = response.data;

        const attestationResponse = await startRegistration({optionsJSON: options});

        const verificationResponse = await BooksitoutServer.post<PasskeyVerificationResult>('/v1/login/passkey/register/finish', attestationResponse);
        const result = verificationResponse.data;

        if (result.verified) {
            return {success: true, loginData: result};
        } else {
            return {success: false, error: result.error || 'Registration failed'};
        }
    } catch (error) {
        return {
            success: false,
            error: error instanceof Error ? error.message : 'Registration failed'
        };
    }
};

export const authenticateWithPasskey = async (): Promise<PasskeyLoginResult> => {
    try {
        if (!isPasskeySupported()) {
            return {success: false, error: 'Passkey not supported on this device'};
        }

        const response = await BooksitoutServer.post<PasskeyAuthenticationOptions>('/v1/login/passkey/login/begin');
        const options = response.data;

        const assertionResponse = await startAuthentication({optionsJSON: options});

        const verificationResponse = await BooksitoutServer.post<LoginSuccessResponse>('/v1/login/passkey/login/finish', assertionResponse);
        const result = verificationResponse.data;

        return {success: true, loginData: result};
    } catch (error) {
        return {
            success: false,
            error: error instanceof Error ? error.message : 'Authentication failed'
        };
    }
};